/**
 * What opens on the calendar — `docs/activity.md` §6.5 (Board 6): the stop
 * card (420), **Block time** after a drag across empty time (340), **New
 * meeting for <first name>** after a click in an empty slot (360), and a time
 * block's own card (*Remove — Just this one · Every week*).
 */
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import {
  BLOCK_TYPES, BLOCK_TYPE_LABELS, KIND_STYLES, firstNameOf, initialsOf, zonedInstant, type BlockType, type CalendarItem,
  type Recurrence,
} from '@rowted/shared'
import { useDismiss } from '../../components/overlays.js'
import { openNewRequest } from '../../requests/open-request.js'
import { cn } from '../../lib/cn.js'
import { activityRequest } from '../api.js'
import { Glyph, type GlyphName } from '../glyphs.js'
import { dayWords } from '../activity-page.js'
import { BottomSheet } from '../sheet.js'
import { useActivity } from '../activity-context.js'
import { Face } from '../faces.js'
import { AttachedTo, type AttachChip } from '../new/attach.js'
import { makeActivity } from '../new/common.js'
import { span } from './look.js'
import { CalendarMenu, itemMenu, menuAtElement, type MenuAt } from './item-menu.js'
import { useBackLayer } from '../use-activity-history.js'
import { tellWords, type TellFacts } from './tell.js'
import { markDone, offersDone } from './done.js'

export type CalendarAction =
  | { kind: 'stop'; item: CalendarItem; anchor: Anchor }
  | { kind: 'block'; item: CalendarItem; anchor: Anchor }
  | { kind: 'task'; item: CalendarItem }
  | { kind: 'blocktime'; personId: string; personName: string | null; date: string; start: number; end: number; anchor: Anchor }
  | { kind: 'slot'; personId: string; personName: string | null; date: string; minutes: number; anchor: Anchor
      /** How long: an hour, unless a phone's edit mode made it longer or shorter (Aaron, Oct 27, answer 6). */
      length?: number }
  | { kind: 'free'; date: string; minutes: number; people: string[]; anchor: Anchor }
  | { kind: 'choose' }

/** Something a card can stand beside: an element, or a point (a slot clicked in empty time). */
export interface Anchor { getBoundingClientRect(): DOMRect }

/** A point to stand beside: the column's right edge at the pointer's height. */
export function pointAnchor(right: number, y: number, left: number, x: number): Anchor {
  void left
  void x
  return { getBoundingClientRect: () => new DOMRect(right - 1, y, 1, 1) }
}

/** Activity's own request to open a sheet: New ▾'s kinds with what to start from (§9). */
export const ACTIVITY_NEW_EVENT = 'rowted:activity-new'
export interface ActivityNewDetail {
  kind: 'text' | 'email' | 'task' | 'meeting' | 'note'
  prefill?: Record<string, unknown>
}
export function openActivityNew(detail: ActivityNewDetail): void {
  window.dispatchEvent(new CustomEvent<ActivityNewDetail>(ACTIVITY_NEW_EVENT, { detail }))
}

/** The people button's chooser, asked for by *+ Add* (§6.1). */
export const ACTIVITY_CHOOSE_EVENT = 'rowted:activity-choose'

/** *10:00–11:30 AM*. */
function timeSpan(start: number, end: number): string {
  return `${span(start, end)} ${end >= 12 * 60 ? 'PM' : 'AM'}`
}

/**
 * A card beside what was clicked: right of it when there is room, else left,
 * kept inside the window. Fixed, portalled, dismissed by Escape or a click
 * outside.
 */
export function FloatingCard({
  anchor, width, onClose, children, label,
}: { anchor: Anchor; width: number; onClose: () => void; children: ReactNode; label: string }) {
  const ref = useRef<HTMLDivElement>(null)
  const [at, setAt] = useState<{ left: number; top: number } | null>(null)
  useDismiss(true, onClose, ref, 40)
  useLayoutEffect(() => {
    const place = () => {
      const rect = anchor.getBoundingClientRect()
      const height = ref.current?.offsetHeight ?? 400
      let left = rect.right + 10
      if (left + width > window.innerWidth - 12) left = Math.max(12, rect.left - width - 10)
      const top = Math.min(Math.max(12, rect.top), Math.max(12, window.innerHeight - height - 12))
      setAt({ left, top })
    }
    place()
    /* Its contents arrive after it opens; it moves to stay whole when they do. */
    const watcher = new ResizeObserver(place)
    if (ref.current) watcher.observe(ref.current)
    return () => watcher.disconnect()
  }, [anchor, width])
  return createPortal(
    <div data-overlay-layer={40} style={{ position: 'fixed', zIndex: 40, left: at?.left ?? -9999, top: at?.top ?? 0, width }}>
      {/* The boards' cards read at `line-height: normal`, not the app's 1.5 (Board 6: 419, 331 and 353 tall). */}
      <div ref={ref} role="dialog" aria-label={label}
        className="overflow-hidden rounded-[14px] border border-line bg-surface leading-[normal] shadow-[0_14px_40px_rgba(20,20,10,.14)]">
        {children}
      </div>
    </div>,
    document.body,
  )
}

/**
 * Where the stop card goes (§10.1): beside what was clicked on a desktop, in a
 * 452 px panel beside the day on an iPad on its side, and as a bottom sheet on
 * one held upright. The card is the same either way.
 */
export type CardPlacement = 'float' | 'dock' | 'sheet'

function CardFrame({
  placement, anchor, width, onClose, label, children,
}: {
  placement: CardPlacement; anchor: Anchor; width: number; onClose: () => void; label: string; children: ReactNode
}) {
  /*
   * On an iPad the card beside the day is the phone's stop page: back closes it (Aaron, Oct 26).
   * The bottom sheet does the same by itself; the desktop's card is a popover, and is not a place.
   */
  useBackLayer(onClose, { enabled: placement === 'dock' })
  if (placement === 'dock') {
    return (
      <aside className="w-[452px] shrink-0 overflow-y-auto border-l border-line bg-surface-alt px-4 py-3.5">
        <div role="dialog" aria-label={label}
          className="w-[420px] overflow-hidden rounded-[14px] border border-line bg-surface leading-[normal] shadow-[0_14px_40px_rgba(20,20,10,.14)]">
          {children}
        </div>
      </aside>
    )
  }
  if (placement === 'sheet') {
    return <BottomSheet label={label} onClose={onClose} bodyClassName="leading-[normal]">{children}</BottomSheet>
  }
  return <FloatingCard anchor={anchor} width={width} onClose={onClose} label={label}>{children}</FloatingCard>
}

function CardHead({ title, sub, onClose, touch = false }: {
  title: string; sub: string; onClose: () => void
  /** On a phone's sheet the ✕ is a finger's 44 px (its circle stays 30). */
  touch?: boolean
}) {
  return (
    <div className="flex items-center gap-3 border-b border-line-faint px-4 py-3.5">
      <div className="flex min-w-0 flex-col">
        <b className="text-[14.5px]">{title}</b>
        <span className="text-[12px] text-text-muted">{sub}</span>
      </div>
      <span className="flex-1" />
      <button type="button" aria-label="Close" onClick={onClose}
        className={cn('inline-flex items-center justify-center rounded-full text-text-muted hover:bg-hover',
          touch ? '-my-2 -mr-2 h-11 w-11' : 'h-[30px] w-[30px]')}>
        <Glyph name="close" size={16} />
      </button>
    </div>
  )
}

function Field({ label, optional, children }: { label: string; optional?: boolean; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="mb-[5px] flex items-center gap-1 text-[11.5px] font-medium text-text-muted">
        {label}{optional && <span className="ml-auto text-text-faint">optional</span>}
      </div>
      {children}
    </div>
  )
}

const BTN = 'inline-flex h-8 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border px-3.5 text-[12.5px] font-semibold'

function Footer({ children }: { children: ReactNode }) {
  return <div className="flex items-center gap-2.5 border-t border-line bg-surface-alt px-4 py-3">{children}</div>
}

export function CalendarOverlays({
  action, onClose, onChanged, me, timeZone, placement = 'float', slotPlacement = 'float', onMenu, onReschedule,
}: {
  action: CalendarAction | null; onClose: () => void; onChanged: () => void; me: string; timeZone: string
  /** Where the stop card opens (§10.1). */
  placement?: CardPlacement
  /** Where New meeting opens: beside the slot, or — on a phone, after a touch and hold (Aaron, Oct 25, note 5) — a bottom sheet. */
  slotPlacement?: 'float' | 'sheet'
  /** The stop card's ⋯: the calendar's own menu for the item (Aaron, Oct 24, note 1.6). */
  onMenu?: (item: CalendarItem, at: MenuAt, card: StopCardAnswer | null) => void
  /** The stop card's **Reschedule**, when the calendar asks *Just this one · Every one* first. */
  onReschedule?: (item: CalendarItem, at: MenuAt) => void
}) {
  useEffect(() => {
    if (action?.kind === 'choose') {
      window.dispatchEvent(new Event(ACTIVITY_CHOOSE_EVENT))
      onClose()
    }
    if (action?.kind === 'task') {
      window.dispatchEvent(new CustomEvent('rowted:activity-task', { detail: { id: action.item.id } }))
      onClose()
    }
  }, [action, onClose])
  if (!action) return null
  if (action.kind === 'stop') {
    return (
      <StopCardView key={action.item.key} action={action} onClose={onClose} onChanged={onChanged} placement={placement}
        onMenu={onMenu} onReschedule={onReschedule} />
    )
  }
  if (action.kind === 'block') return <BlockCard action={action} onClose={onClose} onChanged={onChanged} me={me} />
  if (action.kind === 'blocktime') return <BlockTime action={action} onClose={onClose} onChanged={onChanged} />
  if (action.kind === 'slot') {
    return <QuickMeeting action={action} onClose={onClose} onChanged={onChanged} me={me} timeZone={timeZone} placement={slotPlacement} />
  }
  if (action.kind === 'free') {
    return (
      <QuickMeeting
        action={{ kind: 'slot', personId: action.people[0]!, personName: null, date: action.date, minutes: action.minutes, anchor: action.anchor }}
        people={action.people} onClose={onClose} onChanged={onChanged} me={me} timeZone={timeZone} />
    )
  }
  return null
}

/**
 * The stop card on its own, for a screen that is not the calendar — All
 * activity's site visits and meetings open it beside their row (§8.3: *the
 * activity's sheet*).
 */
export function StopCard({ item, anchor, onClose, onChanged }: {
  item: CalendarItem; anchor: Anchor; onClose: () => void; onChanged: () => void
}) {
  return <StopCardView key={item.key} action={{ kind: 'stop', item, anchor }} onClose={onClose} onChanged={onChanged} placement="float" />
}

// ---------------------------------------------------------------------------
// The stop card (Board 6 A)
// ---------------------------------------------------------------------------

export interface StopCardAnswer {
  id: string
  kind: 'appointment' | 'meeting'
  tags: string[]
  title: string
  date: string
  start: number
  end: number
  personName: string | null
  address: string | null
  /** *412 Orchard Ridge Dr* — the street alone, for the words a message starts with. */
  line1: string | null
  latitude: number | null
  longitude: number | null
  remote: boolean
  drives: string | null
  contact: { id: string; name: string; firstName: string; phone: string | null; phoneKind: string | null
    email: string | null; prefers: 'sms' | 'email' | null; optedOut: boolean } | null
  job: { id: string; reference: string; title: string; status: string; division: string | null; wanted: string | null } | null
  wants: string | null
  done: boolean
  /** A meeting's *<place>* in place of who (Board 20 C). */
  placeWords: string | null
  going: { id: string; name: string; words: string; from: 'home' | 'stop' | null }[] | null
  attached: boolean
  taskList: { id: string; name: string; open: number; quickLabel: string } | null
  /** The occurrence it is about (a repeating meeting's own day), moved by itself or not (0141). */
  occurrence?: string | null
  moved?: boolean
  /** How it repeats, when it does — a repeating meeting is done one occurrence at a time (0152). */
  recurrence?: Recurrence | null
  /** The viewer may change it: they are on it, or may book somebody who is. */
  editable?: boolean
}

/** The stop card for one item — the occurrence's own when it repeats (Aaron, Oct 24, small Q3). */
export function readStop(item: Pick<CalendarItem, 'id' | 'occurrence'>): Promise<StopCardAnswer> {
  return activityRequest<StopCardAnswer>(
    `/activity/stops/${item.id}${item.occurrence ? `?occurrence=${item.occurrence}` : ''}`)
}

/** What a message to the customer about it starts from (`tell.ts`). */
export function tellFactsOf(card: StopCardAnswer): TellFacts | null {
  if (!card.contact) return null
  return {
    kind: card.kind, firstName: card.contact.firstName || firstNameOf(card.contact.name), who: card.personName,
    date: card.date, start: card.start, where: card.line1, remote: card.remote,
  }
}

/**
 * Text or email the customer about it — Activity's own New text or New
 * email, attached to the contact and the job, the words started for them
 * (Aaron, Oct 24, notes 1.7 and 1.8). Nothing goes until **Send**, and it goes
 * through their conversation, so it is in the Inbox.
 */
export function messageAbout(card: StopCardAnswer, channel: 'sms' | 'email', moved: boolean): void {
  const facts = tellFactsOf(card)
  if (!facts || !card.contact) return
  const words = tellWords(facts, moved)
  openActivityNew({
    kind: channel === 'sms' ? 'text' : 'email',
    prefill: {
      contactId: card.contact.id,
      links: [
        { type: 'contact', id: card.contact.id, label: card.contact.name },
        ...(card.job ? [{ type: 'job', id: card.job.id, label: card.job.reference }] : []),
      ],
      body: channel === 'sms' ? words.text : words.email,
      ...(channel === 'email' ? { subject: words.subject } : {}),
    },
  })
}

/** Opens the thing itself — a site visit's booking, a meeting's own sheet, a task's — as a double-click does. */
export function openItemSheet(
  item: Pick<CalendarItem, 'id' | 'kind' | 'jobId'>, jobId: string | null = item.jobId,
  /** One occurrence of a repeating meeting, by its own day — *Edit › Just this one* (Aaron, Oct 27, answer 12). */
  occurrence: string | null = null,
): void {
  if (item.kind === 'appointment' && jobId) openNewRequest({ book: { jobId, appointmentId: item.id } })
  else if (item.kind === 'meeting') {
    openActivityNew({ kind: 'meeting', prefill: { editId: item.id, ...(occurrence ? { occurrence } : {}) } })
  }
  else if (item.kind === 'task') window.dispatchEvent(new CustomEvent('rowted:activity-task', { detail: { id: item.id } }))
}

/** *Meeting* in its kind's blue, a repeat in grey, the rest green (Boards 6 A, 20 C). */
const tagClass = (tag: string): string => (tag === KIND_STYLES.meeting.label ? 'bg-info-bg text-info'
  : tag.startsWith('Every ') ? 'bg-line-soft text-text-muted' : 'bg-accent-soft text-accent-dark')

const STATUS_WORDS: Record<string, string> = {
  requested: 'Requested', reviewed: 'Reviewed', quoted: 'Quoted', approved: 'Approved', scheduled: 'Scheduled',
  in_progress: 'In progress', completed: 'Completed', invoiced: 'Invoiced', paid: 'Paid',
}

function Row({ glyph, children, sub }: { glyph: GlyphName; children: ReactNode; sub?: ReactNode }) {
  return (
    <div className="mt-2.5 flex items-start gap-2.5 text-[13px]">
      <span className="mt-0.5 inline-flex text-text-muted"><Glyph name={glyph} size={15} /></span>
      <div className="flex min-w-0 flex-col">
        <span>{children}</span>
        {sub && <span className="text-[12px] text-text-muted">{sub}</span>}
      </div>
    </div>
  )
}

function Act({ glyph, label, onClick, href }: { glyph: GlyphName; label: string; onClick?: () => void; href?: string }) {
  const body = (
    <>
      <span className="inline-flex text-accent"><Glyph name={glyph} size={17} /></span>{label}
    </>
  )
  const cls = 'flex flex-col items-center gap-1 rounded-[10px] border border-line py-[9px] text-[12px] font-semibold text-text hover:bg-hover'
  return href
    ? <a href={href} className={cls} onClick={onClick} target={href.startsWith('http') ? '_blank' : undefined} rel="noreferrer">{body}</a>
    : <button type="button" className={cls} onClick={onClick}>{body}</button>
}

function StopCardView({
  action, onClose, onChanged, placement, onMenu, onReschedule,
}: {
  action: Extract<CalendarAction, { kind: 'stop' }>; onClose: () => void; onChanged: () => void; placement: CardPlacement
  onMenu?: (item: CalendarItem, at: MenuAt, card: StopCardAnswer | null) => void
  onReschedule?: (item: CalendarItem, at: MenuAt) => void
}) {
  const navigate = useNavigate()
  const { directory, me } = useActivity()
  const [card, setCard] = useState<StopCardAnswer | null>(null)
  /* Off the calendar (All activity) the ⋯ is this card's own menu, with what it can do from here. */
  const [menuAt, setMenuAt] = useState<MenuAt | null>(null)
  const meeting = action.item.kind === 'meeting'
  useEffect(() => {
    let live = true
    readStop(action.item)
      .then((answer) => { if (live) setCard(answer) })
      .catch(() => { if (live) setCard(null) })
    return () => { live = false }
  }, [action.item.id, action.item.occurrence])

  const done = async () => {
    /* That occurrence alone, for a repeating meeting (0152). */
    await markDone({ id: action.item.id, kind: action.item.kind, occurrence: card?.occurrence ?? action.item.occurrence })
    onChanged()
    onClose()
  }
  const reschedule = (event?: React.MouseEvent<HTMLElement>) => {
    onClose()
    if (onReschedule) {
      const rect = event?.currentTarget.getBoundingClientRect()
      onReschedule(action.item, rect ? { x: rect.left, y: rect.bottom + 4 } : menuAtElement(document.body))
      return
    }
    /* The calendar's item already knows its kind and job, so a press before the card has loaded opens the right sheet. */
    openItemSheet(action.item, card?.job?.id ?? action.item.jobId)
  }
  const openMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    const rect = event.currentTarget.getBoundingClientRect()
    const at = { x: rect.left, y: rect.bottom + 4 }
    if (onMenu) onMenu(action.item, at, card)
    else setMenuAt(at)
  }
  const canSend = directory ? directory.people.some((person) => person.id === me.user.id && person.inbox) : true
  /*
   * Off the calendar the item is drawn from a list row, which cannot say
   * whether the viewer may change it (All activity's says not), so the card's
   * own answer does — or the ⋯ on a meeting there offered nothing and did
   * nothing (Aaron, Oct 25, phone note 4).
   */
  const ownItem = card?.editable ? { ...action.item, editable: true } : action.item
  const ownMenu = menuAt ? itemMenu(ownItem, {
    edit: () => { onClose(); openItemSheet(action.item, card?.job?.id ?? action.item.jobId) },
    reschedule: () => reschedule(),
    message: card ? (channel) => { onClose(); messageAbout(card, channel, false) } : undefined,
    tick: card?.done || (card && !offersDone(card)) ? undefined : () => void done(),
    openJob: (id) => navigate(`/jobs/${id}`),
  }, { contact: card?.contact ?? null, canSend, done: card?.done }) : []
  const maps = card?.latitude !== null && card?.latitude !== undefined
    ? `https://www.google.com/maps/dir/?api=1&destination=${card.latitude},${card.longitude}` : undefined
  const inbox = (channel: 'sms' | 'email') => {
    onClose()
    if (card?.contact) navigate(`/activity?tab=inbox&contact=${card.contact.id}&channel=${channel}`)
  }

  return (
    <CardFrame placement={placement} anchor={action.anchor} width={420} onClose={onClose} label={action.item.title}>
      <div className="border-b border-line-faint px-4 py-3.5">
        <div className="flex items-center gap-1.5">
          {(card?.tags ?? [action.item.kind === 'appointment' ? 'Site visit' : 'Meeting']).map((tag) => (
            <span key={tag} className={cn('inline-flex h-5 items-center rounded px-1.5 text-[11px] font-semibold', tagClass(tag))}>{tag}</span>
          ))}
          <span className="flex-1" />
          <button type="button" aria-label="More" aria-haspopup="menu" onClick={openMenu}
            className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-line text-text hover:bg-hover">
            <Glyph name="more" size={15} />
          </button>
          {menuAt && <CalendarMenu items={ownMenu} at={menuAt} label={`${action.item.title} — more`} onClose={() => setMenuAt(null)} />}
          <button type="button" aria-label="Close" onClick={onClose} className="inline-flex text-text-muted"><Glyph name="close" size={16} /></button>
        </div>
        <b className="mt-2 block text-[16px]">{card?.title ?? action.item.title}</b>
        <span className="text-[12.5px] text-text-muted">
          {dayWords(card?.date ?? action.item.date)} · {timeSpan(card?.start ?? action.item.start, card?.end ?? action.item.end)}
          {meeting ? (card?.placeWords ? ` · ${card.placeWords}` : '') : card?.personName ? ` · ${card.personName}` : ''}
        </span>
      </div>
      {meeting && card ? <MeetingBody card={card} onReschedule={(event) => reschedule(event)} onOpenList={(id) => {
        onClose()
        navigate(`/activity?tab=tasks&view=board&list=${id}`)
      }} onOpenJob={(id) => navigate(`/jobs/${id}`)} actions={(
        <div className="mt-3.5 grid grid-cols-4 gap-2">
          <Act glyph="navigate" label="Navigate" href={maps} />
          <Act glyph="phone" label="Call" href={card.contact?.phone ? `tel:${card.contact.phone}` : undefined} />
          <Act glyph="bubble" label="Text" onClick={() => inbox('sms')} />
          <Act glyph="envelope" label="Email" onClick={() => inbox('email')} />
        </div>
      )} /> : (
      <div className="px-4 pb-4 pt-1">
        {card?.address && !card.remote && (
          <Row glyph="pin" sub={card.drives ?? undefined}><b>{card.address}</b></Row>
        )}
        {card?.contact && (
          <Row glyph="user" sub={[card.contact.phone, card.contact.phoneKind,
            card.contact.prefers ? `prefers ${card.contact.prefers === 'sms' ? 'Text' : 'Email'}` : null].filter(Boolean).join(' · ')}>
            {card.contact.name}
          </Row>
        )}
        {card?.job && (
          <Row glyph="layers" sub={[STATUS_WORDS[card.job.status] ?? card.job.status, card.job.division, card.job.wanted].filter(Boolean).join(' · ')}>
            {card.job.reference} · {card.job.title}
          </Row>
        )}
        {card?.wants && <Row glyph="file" sub="What they want">{card.wants}</Row>}
        <div className="mt-3.5 grid grid-cols-4 gap-2">
          <Act glyph="navigate" label="Navigate" href={maps} />
          <Act glyph="phone" label="Call" href={card?.contact?.phone ? `tel:${card.contact.phone}` : undefined} />
          <Act glyph="bubble" label="Text" onClick={() => inbox('sms')} />
          <Act glyph="envelope" label="Email" onClick={() => inbox('email')} />
        </div>
        <div className="mt-3 flex gap-2">
          {card?.job && (
            <button type="button" className={cn(BTN, 'h-[34px] border-line bg-surface')} onClick={() => navigate(`/jobs/${card.job!.id}`)}>
              Open the job
            </button>
          )}
          <button type="button" className={cn(BTN, 'h-[34px] border-line bg-surface')} onClick={(event) => reschedule(event)}>
            <Glyph name="calendar" size={14.5} />Reschedule
          </button>
          <span className="flex-1" />
          {!card?.done && (!card || offersDone(card)) && (
            <button type="button" className={cn(BTN, 'h-[34px] border-accent bg-accent text-white')} onClick={() => void done()}>
              <Glyph name="check" size={14.5} />Done
            </button>
          )}
        </div>
      </div>
      )}
    </CardFrame>
  )
}

/**
 * A meeting opened (Board 20 C): *<n> going* — each person and where their
 * day reaches it from — whether it is attached to anything, the task list
 * that meets at it, and **Open the task list · Reschedule**. A meeting with a
 * customer keeps the customer's rows and the four buttons.
 */
function MeetingBody({
  card, actions, onReschedule, onOpenList, onOpenJob,
}: {
  card: StopCardAnswer; actions: ReactNode; onReschedule: (event: React.MouseEvent<HTMLElement>) => void
  onOpenList: (id: string) => void
  onOpenJob: (id: string) => void
}) {
  const { directory } = useActivity()
  const faces = new Map((directory?.people ?? []).map((person) => [person.id, person]))
  return (
    <>
      <div className="px-4 pb-1 pt-1.5">
        {card.going && card.going.length > 0 && (
          <>
            <div className="mb-0.5 mt-1.5 text-[11px] font-semibold uppercase tracking-[.06em] text-text-faint">
              {card.going.length} going
            </div>
            {card.going.map((person) => (
              /* 34 px and its hairline under it, as the board draws a row (35 apart). */
              <div key={person.id} className="flex h-[35px] items-center gap-2.5 border-b border-line-faint text-[13px]">
                <Face person={faces.get(person.id) ?? { id: person.id, name: person.name, initials: initialsOf(person.name), colour: null }} size={24} />
                <b className="font-semibold">{person.name}</b>
                <span className="flex-1" />
                <span className="inline-flex items-center gap-1 text-[11.5px] text-text-muted">
                  {person.from && <Glyph name={person.from === 'home' ? 'house' : 'car'} size={12} />}
                  {person.words}
                </span>
              </div>
            ))}
          </>
        )}
        {card.address && !card.remote && card.contact && (
          <Row glyph="pin" sub={card.drives ?? undefined}><b>{card.address}</b></Row>
        )}
        {card.contact && (
          <Row glyph="user" sub={[card.contact.phone, card.contact.phoneKind,
            card.contact.prefers ? `prefers ${card.contact.prefers === 'sms' ? 'Text' : 'Email'}` : null].filter(Boolean).join(' · ')}>
            {card.contact.name}
          </Row>
        )}
        {card.job && (
          <Row glyph="layers" sub={[STATUS_WORDS[card.job.status] ?? card.job.status, card.job.division, card.job.wanted].filter(Boolean).join(' · ')}>
            {card.job.reference} · {card.job.title}
          </Row>
        )}
        {!card.attached && (
          <div className="mt-3 flex items-start gap-2.5 text-[12.5px] text-text-muted">
            <span className="mt-px inline-flex"><Glyph name="link" size={14} /></span>
            <span>Attached to nothing — a meeting inside the company</span>
          </div>
        )}
        {card.taskList && (
          <div className="mt-2 flex items-start gap-2.5 text-[12.5px] text-text-body">
            <span className="mt-px inline-flex text-text-muted"><Glyph name="board" size={14} /></span>
            <span>Task list <b>{card.taskList.name}</b> · {card.taskList.open} open — its tasks can be due “{card.taskList.quickLabel}”</span>
          </div>
        )}
        {card.contact && actions}
      </div>
      <div className="flex gap-2 px-4 pb-4 pt-3">
        {card.taskList && (
          <button type="button" className={cn(BTN, 'h-[34px] border-line bg-surface')} onClick={() => onOpenList(card.taskList!.id)}>
            <Glyph name="board" size={14.5} />Open the task list
          </button>
        )}
        {card.job && (
          <button type="button" className={cn(BTN, 'h-[34px] border-line bg-surface')} onClick={() => onOpenJob(card.job!.id)}>
            Open the job
          </button>
        )}
        <button type="button" className={cn(BTN, 'h-[34px] border-line bg-surface')} onClick={onReschedule}>
          <Glyph name="calendar" size={14.5} />Reschedule
        </button>
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// Block time (Board 6 C) and a block's own card
// ---------------------------------------------------------------------------

function TypeChips({ value, onChange }: { value: BlockType; onChange: (value: BlockType) => void }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {BLOCK_TYPES.map((type) => (
        <button key={type} type="button" onClick={() => onChange(type)}
          className={cn('inline-flex h-[30px] items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 text-[12px] font-semibold',
            type === value ? 'border-accent-line bg-accent-soft text-accent-dark' : 'border-line bg-surface text-text')}>
          {type === value && <Glyph name="check" size={12} />}{BLOCK_TYPE_LABELS[type]}
        </button>
      ))}
    </div>
  )
}

function Box({ on }: { on: boolean }) {
  return (
    <span className={cn('inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px] border-[1.5px] text-white',
      on ? 'border-accent bg-accent' : 'border-line-strong bg-surface')}>
      {on && <Glyph name="check" size={12} stroke={3} />}
    </span>
  )
}

function BlockTime({
  action, onClose, onChanged,
}: { action: Extract<CalendarAction, { kind: 'blocktime' }>; onClose: () => void; onChanged: () => void }) {
  const [type, setType] = useState<BlockType>('office')
  const [note, setNote] = useState('')
  const [weekly, setWeekly] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const save = async () => {
    try {
      await activityRequest('/activity/blocks', {
        method: 'POST',
        body: { personId: action.personId, type, note, date: action.date, start: action.start,
          minutes: action.end - action.start, everyWeek: weekly },
      })
      onChanged()
      onClose()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    }
  }
  return (
    <FloatingCard anchor={action.anchor} width={340} onClose={onClose} label="Block time">
      <CardHead title="Block time" sub={`${dayWords(action.date)} · ${timeSpan(action.start, action.end)}`} onClose={onClose} />
      <div className="flex flex-col gap-3 px-4 py-3.5">
        <Field label="What"><TypeChips value={type} onChange={setType} /></Field>
        <Field label="Note" optional>
          <input aria-label="Note" value={note} onChange={(event) => setNote(event.target.value)}
            className="h-9 w-full rounded-lg border border-line px-[11px] text-[13px] outline-none focus:border-accent" />
        </Field>
        <button type="button" role="checkbox" aria-checked={weekly} onClick={() => setWeekly((v) => !v)}
          className="flex items-center gap-2 text-[12.5px]"><Box on={weekly} />Every week</button>
        {error && <span className="text-[12px] text-danger">{error}</span>}
      </div>
      <Footer>
        <span className="flex-1" />
        <button type="button" className={cn(BTN, 'border-line bg-surface')} onClick={onClose}>Cancel</button>
        <button type="button" className={cn(BTN, 'border-accent bg-accent text-white')} onClick={() => void save()}>
          <Glyph name="check" size={14.5} />Block it
        </button>
      </Footer>
    </FloatingCard>
  )
}

function BlockCard({
  action, onClose, onChanged,
}: { action: Extract<CalendarAction, { kind: 'block' }>; onClose: () => void; onChanged: () => void; me: string }) {
  const item = action.item
  const [asking, setAsking] = useState(false)
  const remove = async (occurrence: string | null) => {
    await activityRequest(`/activity/blocks/${item.id}/remove`, { method: 'POST', body: { occurrence } })
    onChanged()
    onClose()
  }
  return (
    <FloatingCard anchor={action.anchor} width={340} onClose={onClose} label={item.title}>
      <CardHead title={item.title} sub={`${dayWords(item.date)} · ${timeSpan(item.start, item.end)}${item.repeatRule ? ' · every week' : ''}`} onClose={onClose} />
      <Footer>
        {asking ? (
          <>
            <span className="text-[12.5px] font-semibold">Remove</span>
            <span className="flex-1" />
            <button type="button" className={cn(BTN, 'border-line bg-surface')} onClick={() => void remove(item.occurrence ?? item.date)}>Just this one</button>
            <button type="button" className={cn(BTN, 'border-line bg-surface text-danger')} onClick={() => void remove(null)}>Every week</button>
          </>
        ) : (
          <>
            <span className="flex-1" />
            <button type="button" className={cn(BTN, 'border-line bg-surface text-danger')}
              onClick={() => (item.repeatRule ? setAsking(true) : void remove(null))}>Remove</button>
          </>
        )}
      </Footer>
    </FloatingCard>
  )
}

// ---------------------------------------------------------------------------
// New meeting for <first name> (Board 6 D)
// ---------------------------------------------------------------------------

function QuickMeeting({
  action, people, onClose, onChanged, me, timeZone, placement = 'float',
}: {
  action: Extract<CalendarAction, { kind: 'slot' }>; people?: string[]; onClose: () => void; onChanged: () => void
  me: string; timeZone: string
  /** A bottom sheet on a phone, its controls at a finger's size; beside the slot everywhere else. */
  placement?: 'float' | 'sheet'
}) {
  const sheet = placement === 'sheet'
  const start = Math.floor(action.minutes / 15) * 15
  const length = action.length ?? 60
  const end = start + length
  const [title, setTitle] = useState('')
  /*
   * One search, New request's (Aaron, Oct 23, note 4) — and since his Oct 24
   * notes 1.1 and 1.2 it is *Attached to*, the same box as every New sheet's:
   * the chips in it are what the meeting is attached to, the first place
   * among them is where it is and the first contact who it is with. An
   * address is in More options (small question 2: "more options is fine").
   */
  const [chips, setChips] = useState<AttachChip[]>([])
  const place = chips.find((chip) => chip.type === 'property') ?? null
  const contact = chips.find((chip) => chip.type === 'contact') ?? null
  const [error, setError] = useState<string | null>(null)
  const who = people ?? [action.personId]
  const heading = people && people.length > 1 ? `New meeting · ${people.length} people`
    : action.personId === me || !action.personName ? 'New meeting' : `New meeting for ${firstNameOf(action.personName)}`

  const more = () => {
    onClose()
    openActivityNew({ kind: 'meeting', prefill: { title, date: action.date, start, minutes: length, people: who,
      links: chips.map((chip) => ({ type: chip.type, id: chip.id, label: chip.label })), linksExact: true,
      contact: contact ? { id: contact.id, label: contact.label } : null,
      place: place ? { id: place.id, label: place.label } : null } })
  }
  const save = async () => {
    if (!title.trim()) { setError('Give it a title.'); return }
    const body = {
      kind: 'meeting', title: title.trim(), dueAt: zonedInstant(action.date, start, timeZone).toISOString(),
      durationMinutes: length, participantIds: who, visibility: 'internal',
      locationKind: place ? 'property' : 'office', locationPropertyId: place?.id ?? null,
    }
    try {
      /* Written on the first chip with the rest as links (`expand: false`), or a team item with none. */
      await makeActivity(body, chips)
      onChanged()
      onClose()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    }
  }

  return (
    <CardFrame placement={sheet ? 'sheet' : 'float'} anchor={action.anchor} width={360} onClose={onClose} label={heading}>
      <CardHead title={heading} sub={`${dayWords(action.date)} · ${timeSpan(start, end)}`} onClose={onClose} touch={sheet} />
      <div className="flex flex-col gap-2.5 px-4 py-3.5">
        <Field label="Title">
          <input autoFocus aria-label="Title" value={title} onChange={(event) => setTitle(event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Enter') void save() }}
            className={cn('w-full rounded-lg border border-accent px-[11px] shadow-[0_0_0_3px_rgb(var(--color-accent-ring))] outline-none',
              sheet ? 'h-11 text-[15px]' : 'h-9 text-[13px]')} />
        </Field>
        <Field label="Attached to" optional>
          <AttachedTo chips={chips} onChange={(next) => setChips(next)} phone={sheet} required={false} compact={!sheet} label={false}
            hint={place ? 'the city shows on the calendar and the drive notes use it'
              : 'a contact brings their place · an address is in More options'} />
        </Field>
        {error && <span className="text-[12px] text-danger">{error}</span>}
      </div>
      <Footer>
        <button type="button" className={cn('inline-flex items-center px-1.5 font-semibold text-accent', sheet ? 'h-11 text-[14px]' : 'h-[30px] text-[12.5px]')}
          onClick={more}>More options</button>
        <span className="flex-1" />
        <button type="button" onClick={() => void save()}
          className={sheet ? 'inline-flex h-11 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-accent bg-accent px-5 text-[14px] font-semibold text-white'
            : cn(BTN, 'border-accent bg-accent text-white')}>
          <Glyph name="check" size={14.5} />Save
        </button>
      </Footer>
    </CardFrame>
  )
}
